import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { History, RefreshCw, ChevronLeft, ChevronRight, Search, CheckCircle, XCircle, Clock, AlertTriangle, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EmailLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const limit = 20;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { limit, skip: page * limit };
      if (statusFilter) params.status = statusFilter;
      
      const response = await axios.get(`${API}/email/logs`, { params });
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, statusFilter]);

  const totalPages = Math.ceil(total / limit);

  const filteredLogs = searchTerm 
    ? logs.filter(log => 
        log.recipient_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.subject?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : logs;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      sent: 'badge-sent',
      delivered: 'badge-delivered',
      pending: 'badge-pending',
      failed: 'badge-failed',
      bounced: 'badge-bounced'
    };
    return badges[status] || 'badge-pending';
  };

  const getStatusIcon = (status) => {
    const icons = {
      sent: <Send className="w-4 h-4" />,
      delivered: <CheckCircle className="w-4 h-4" />,
      pending: <Clock className="w-4 h-4" />,
      failed: <XCircle className="w-4 h-4" />,
      bounced: <AlertTriangle className="w-4 h-4" />
    };
    return icons[status] || <Clock className="w-4 h-4" />;
  };

  const updateStatus = async (logId, newStatus) => {
    try {
      await axios.put(`${API}/email/logs/${logId}/status?status=${newStatus}`);
      toast.success(`Status updated to ${newStatus}`);
      fetchLogs();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  return (
    <div className="p-6 md:p-8 animate-fade-in" data-testid="email-logs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">
            Email Logs
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Track delivery status of all sent emails
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchLogs}
          className="rounded-lg border-[#222] text-slate-400 hover:text-white hover:bg-[#1a1a1a] w-fit"
          data-testid="btn-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-[#111] border-[#1a1a1a] rounded-lg mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search by email or subject..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-10 rounded-lg border-[#222] bg-[#0d0d0d] text-white focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                data-testid="input-search"
              />
            </div>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full md:w-48 h-10 rounded-lg border-[#222] bg-[#0d0d0d] text-white" data-testid="select-status">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent className="bg-[#111] border-[#222]">
                <SelectItem value="all" className="text-slate-300">All Statuses</SelectItem>
                <SelectItem value="sent" className="text-cyan-400">Sent</SelectItem>
                <SelectItem value="delivered" className="text-green-400">Delivered</SelectItem>
                <SelectItem value="pending" className="text-amber-400">Pending</SelectItem>
                <SelectItem value="failed" className="text-red-400">Failed</SelectItem>
                <SelectItem value="bounced" className="text-orange-400">Bounced</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-[#111] border-[#1a1a1a] rounded-lg">
        <CardHeader className="border-b border-[#1a1a1a] pb-4">
          <CardTitle className="font-heading text-xl font-semibold flex items-center gap-2 text-white">
            <History className="w-5 h-5 text-cyan-400" />
            Email History
            <span className="ml-2 text-sm font-normal text-slate-500">
              ({total} total)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="data-table w-full" data-testid="logs-table">
              <thead>
                <tr>
                  <th>Recipient</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Sent At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-12 text-slate-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredLogs.length > 0 ? (
                  filteredLogs.map((log, index) => (
                    <tr key={log.id || index} className="transition-colors">
                      <td className="font-mono text-sm text-white">
                        {log.recipient_email}
                      </td>
                      <td className="text-sm text-slate-400 max-w-xs truncate">
                        {log.subject}
                      </td>
                      <td>
                        <span className={getStatusBadge(log.status)}>
                          {log.status}
                        </span>
                      </td>
                      <td className="font-mono text-xs text-slate-500">
                        {formatDate(log.sent_at)}
                      </td>
                      <td>
                        <Select 
                          value={log.status} 
                          onValueChange={(value) => updateStatus(log.id, value)}
                        >
                          <SelectTrigger className="h-8 w-28 text-xs border-[#222] bg-transparent">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#111] border-[#222]">
                            <SelectItem value="sent" className="text-cyan-400">Sent</SelectItem>
                            <SelectItem value="delivered" className="text-green-400">Delivered</SelectItem>
                            <SelectItem value="pending" className="text-amber-400">Pending</SelectItem>
                            <SelectItem value="failed" className="text-red-400">Failed</SelectItem>
                            <SelectItem value="bounced" className="text-orange-400">Bounced</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center py-12 text-slate-500">
                      No email logs found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-[#1a1a1a]">
              <p className="text-sm text-slate-500">
                Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="rounded-lg border-[#222] text-slate-400"
                  data-testid="btn-prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-500 px-2">
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="rounded-lg border-[#222] text-slate-400"
                  data-testid="btn-next-page"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailLogs;
